import { CONFIG } from '../config/environment';

console.log('🚀 API.TS FILE LOADED - VERSION 2');

class ApiError extends Error {
  constructor(public status: number, message: string, public data?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchWithErrorHandling(url: string, options?: RequestInit) {
  try {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    console.log('🔑 Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'NOT FOUND');
    console.log('📦 All localStorage keys:', Object.keys(localStorage));

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add existing headers
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('✅ Authorization header added');
    } else {
      console.warn('⚠️ No token found in localStorage - request will be unauthorized');
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        localStorage.removeItem('token');
        // Optionally trigger logout or redirect
      }
      throw new ApiError(response.status, data.error || `HTTP ${response.status}`, data);
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, 'Network error', { originalError: error });
  }
}

export const api = {
  sheets: {
    async fetchTrackedBookings(): Promise<any[][]> {
      try {
        const response = await fetch(`${CONFIG.API_BASE_URL}/api/sheets/tracked-bookings`);
        const data = await response.json();
        
        // Handle 503 with mock data
        if (response.status === 503 && data.mock && data.values) {
          console.warn('Google Sheets not configured, using mock data from backend');
          return data.values;
        }
        
        // Handle success
        if (response.ok && data.values) {
          return data.values;
        }
        
        // Handle other errors
        if (!response.ok) {
          throw new ApiError(response.status, data.error || `HTTP ${response.status}`, data);
        }
        
        return data.values || [];
      } catch (error) {
        console.error('Error fetching tracked bookings:', error);
        // Re-throw to let the service handle with its own mock data
        throw error;
      }
    },

    async fetchPayments(): Promise<any[][]> {
      try {
        const response = await fetch(`${CONFIG.API_BASE_URL}/api/sheets/payments`);
        const data = await response.json();
        
        // Handle 503 with mock data
        if (response.status === 503 && data.mock && data.values) {
          console.warn('Google Sheets not configured, using mock payments data from backend');
          return data.values;
        }
        
        // Handle success
        if (response.ok && data.values) {
          return data.values;
        }
        
        // Handle other errors
        if (!response.ok) {
          throw new ApiError(response.status, data.error || `HTTP ${response.status}`, data);
        }
        
        return data.values || [];
      } catch (error) {
        console.error('Error fetching payments:', error);
        // Re-throw to let the service handle with its own mock data
        throw error;
      }
    },

    async fetchUsers(): Promise<any[][]> {
      try {
        const response = await fetch(`${CONFIG.API_BASE_URL}/api/sheets/users`);
        const data = await response.json();
        
        // Handle 503 with mock data
        if (response.status === 503 && data.mock && data.values) {
          console.warn('Google Sheets not configured, using mock users data from backend');
          return data.values;
        }
        
        // Handle success
        if (response.ok && data.values) {
          return data.values;
        }
        
        // Handle other errors
        if (!response.ok) {
          throw new ApiError(response.status, data.error || `HTTP ${response.status}`, data);
        }
        
        return data.values || [];
      } catch (error) {
        console.error('Error fetching users:', error);
        // Re-throw to let the service handle with its own mock data
        throw error;
      }
    },
  },

  payments: {
    async fetchByOrgId(orgId: string, limit = 100, offset = 0): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/payments/${orgId}?limit=${limit}&offset=${offset}`
        );

        return data;
      } catch (error) {
        console.error('Error fetching payments from database:', error);
        throw error;
      }
    },

    async add(payment: {
      orgId: string;
      smLink: string;
      firstName: string;
      lastName: string;
      amount: string;
      paymentDate: string;
    }): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/sheets/payments`,
          {
            method: 'POST',
            body: JSON.stringify(payment),
          }
        );

        return data;
      } catch (error) {
        console.error('Error adding payment:', error);
        throw error;
      }
    },

    async delete(orgId: string, paymentId: number): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/payments/${orgId}/${paymentId}`,
          {
            method: 'DELETE',
          }
        );

        return data;
      } catch (error) {
        console.error('Error deleting payment:', error);
        throw error;
      }
    },
  },

  bookings: {
    async fetchByOrgId(orgId: string, limit = 10000, offset = 0): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/bookings/${orgId}?limit=${limit}&offset=${offset}`
        );

        return data;
      } catch (error) {
        console.error('Error fetching bookings from database:', error);
        throw error;
      }
    },

    async add(booking: {
      orgId: string;
      smLink: string;
      dateTime: string;
      trackableLink?: string;
      firstName: string;
      lastName: string;
      email?: string;
      closed?: string;
      closeAmount?: string;
    }): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/bookings`,
          {
            method: 'POST',
            body: JSON.stringify(booking),
          }
        );

        return data;
      } catch (error) {
        console.error('Error adding booking:', error);
        throw error;
      }
    },

    async updateClosed(orgId: string, bookingId: number, closed: string, closeAmount: string): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/bookings/${orgId}/${bookingId}`,
          {
            method: 'PATCH',
            body: JSON.stringify({ closed, closeAmount }),
          }
        );

        return data;
      } catch (error) {
        console.error('Error updating booking:', error);
        throw error;
      }
    },

    async delete(orgId: string, bookingId: number): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/bookings/${orgId}/${bookingId}`,
          {
            method: 'DELETE',
          }
        );

        return data;
      } catch (error) {
        console.error('Error deleting booking:', error);
        throw error;
      }
    },

    async fetchVideoAnalytics(orgId: string, videoUrl: string, granularity: 'hourly' | 'daily' = 'daily'): Promise<any> {
      try {
        const encodedVideoUrl = encodeURIComponent(videoUrl);
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/bookings/${orgId}/video-analytics?videoUrl=${encodedVideoUrl}&granularity=${granularity}`
        );

        return data;
      } catch (error) {
        console.error('Error fetching video analytics:', error);
        throw error;
      }
    },

    async fetchByVideo(orgId: string, videoUrl: string, startDate?: string, endDate?: string, limit = 1000, offset = 0): Promise<any> {
      try {
        const encodedVideoUrl = encodeURIComponent(videoUrl);
        let url = `${CONFIG.API_BASE_URL}/api/bookings/${orgId}/by-video?videoUrl=${encodedVideoUrl}&limit=${limit}&offset=${offset}`;

        if (startDate) {
          url += `&startDate=${encodeURIComponent(startDate)}`;
        }
        if (endDate) {
          url += `&endDate=${encodeURIComponent(endDate)}`;
        }

        const data = await fetchWithErrorHandling(url);
        return data;
      } catch (error) {
        console.error('Error fetching bookings by video:', error);
        throw error;
      }
    },
  },

  ai: {
    async analyze(content: string, socialMediaLink?: string, customPrompt?: string): Promise<string> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/ai/analyze`,
          {
            method: 'POST',
            body: JSON.stringify({
              content,
              socialMediaLink,
              customPrompt,
            }),
          }
        );

        return data.analysis;
      } catch (error) {
        console.error('Error analyzing content:', error);
        throw error;
      }
    },
  },

  trackableLinks: {
    async fetchByOrgId(orgId: string, limit = 10000, offset = 0): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/trackable-links/${orgId}?limit=${limit}&offset=${offset}`
        );

        return data;
      } catch (error) {
        console.error('Error fetching trackable links from database:', error);
        throw error;
      }
    },

    async validateDestination(destinationUrl: string): Promise<{
      ok: boolean;
      hasTypeform: boolean;
      reason?: string;
      status?: number;
    }> {
      try {
        return await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/trackable-links/validate-destination`,
          {
            method: 'POST',
            body: JSON.stringify({ destinationUrl }),
          }
        );
      } catch (error) {
        console.error('Error validating destination URL:', error);
        // Network/server failure: treat as "couldn't verify" so the caller
        // drops the user into step 3 with a soft warning instead of blocking.
        return { ok: false, hasTypeform: false, reason: 'network_error' };
      }
    },

    async add(trackableLink: {
      orgId: string;
      smLink: string;
      destinationUrl: string;
      offer?: string;
    }): Promise<any> {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/trackable-links/${trackableLink.orgId}`,
          {
            method: 'POST',
            body: JSON.stringify({
              smLink: trackableLink.smLink,
              destinationUrl: trackableLink.destinationUrl,
              offer: trackableLink.offer,
            }),
          }
        );

        return data;
      } catch (error) {
        console.error('Error creating trackable link:', error);
        throw error;
      }
    },
  },

  // Subscription/Stripe API methods
  subscription: {
    async getPlans() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/stripe/plans`
        );
        return data;
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }
    },

    async createCheckoutSession(planTier: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/stripe/create-checkout-session`,
          {
            method: 'POST',
            body: JSON.stringify({ planTier }),
          }
        );
        return data;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        throw error;
      }
    },

    async getSubscription() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/stripe/subscription`
        );
        return data;
      } catch (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }
    },

    async cancelSubscription() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/stripe/cancel-subscription`,
          {
            method: 'POST',
          }
        );
        return data;
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        throw error;
      }
    },
  },

  // Integrations waitlist methods
  integrations: {
    async joinWaitlist() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/stripe/integrations/waitlist`,
          {
            method: 'POST',
          }
        );
        return data;
      } catch (error) {
        console.error('Error joining integrations waitlist:', error);
        throw error;
      }
    },

    async checkWaitlistStatus() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/stripe/integrations/waitlist/status`
        );
        return data;
      } catch (error) {
        console.error('Error checking waitlist status:', error);
        throw error;
      }
    },
  },

  // Onboarding API methods
  onboarding: {
    async getStatus() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/onboarding/status`
        );
        return data;
      } catch (error) {
        console.error('Error fetching onboarding status:', error);
        throw error;
      }
    },

    async saveQuestionnaire(howHeardAboutUs: string, platformUseCase: string, additionalDetails: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/onboarding/questionnaire`,
          {
            method: 'POST',
            body: JSON.stringify({ howHeardAboutUs, platformUseCase, additionalDetails }),
          }
        );
        return data;
      } catch (error) {
        console.error('Error saving questionnaire:', error);
        throw error;
      }
    },

    async completeOnboarding() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/onboarding/complete`,
          {
            method: 'POST',
          }
        );
        return data;
      } catch (error) {
        console.error('Error completing onboarding:', error);
        throw error;
      }
    },
  },

  // Webhook API methods
  webhooks: {
    async create(formName: string, description?: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/typeform-webhooks/create`,
          {
            method: 'POST',
            body: JSON.stringify({ formName, description }),
          }
        );
        return data;
      } catch (error) {
        console.error('Error creating webhook:', error);
        throw error;
      }
    },

    async fetchByOrgId(orgId: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/typeform-webhooks/${orgId}`
        );
        return data;
      } catch (error) {
        console.error('Error fetching webhooks:', error);
        throw error;
      }
    },

    async delete(orgId: string, webhookId: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/typeform-webhooks/${orgId}/${webhookId}`,
          {
            method: 'DELETE',
          }
        );
        return data;
      } catch (error) {
        console.error('Error deleting webhook:', error);
        throw error;
      }
    },
  },

  // Chat API methods
  chat: {
    async createSession() {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/chat/sessions`,
          {
            method: 'POST',
          }
        );
        return data;
      } catch (error) {
        console.error('Error creating chat session:', error);
        throw error;
      }
    },

    async sendMessage(sessionId: string, message: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/chat/message`,
          {
            method: 'POST',
            body: JSON.stringify({ sessionId, message }),
          }
        );
        return data;
      } catch (error) {
        console.error('Error sending chat message:', error);
        throw error;
      }
    },

    async getHistory(sessionId: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/chat/sessions/${sessionId}`
        );
        return data;
      } catch (error) {
        console.error('Error fetching chat history:', error);
        throw error;
      }
    },

    async deleteSession(sessionId: string) {
      try {
        const data = await fetchWithErrorHandling(
          `${CONFIG.API_BASE_URL}/api/chat/sessions/${sessionId}`,
          {
            method: 'DELETE',
          }
        );
        return data;
      } catch (error) {
        console.error('Error deleting chat session:', error);
        throw error;
      }
    },
  },

  // Creator identity API
  identity: {
    async get() {
      try {
        const data = await fetchWithErrorHandling(`${CONFIG.API_BASE_URL}/api/identity`);
        return data;
      } catch (error) {
        console.error('Error fetching identity:', error);
        throw error;
      }
    },
    async set(text: string) {
      try {
        const data = await fetchWithErrorHandling(`${CONFIG.API_BASE_URL}/api/identity`, {
          method: 'POST',
          body: JSON.stringify({ text }),
        });
        return data;
      } catch (error) {
        console.error('Error setting identity:', error);
        throw error;
      }
    },
    async clear() {
      try {
        const data = await fetchWithErrorHandling(`${CONFIG.API_BASE_URL}/api/identity`, {
          method: 'DELETE',
        });
        return data;
      } catch (error) {
        console.error('Error clearing identity:', error);
        throw error;
      }
    },
  },
};